import { useCallback, useMemo, useState } from 'react'
import Logo from '@app/assets/images/logo.svg?url'
import AlchemyLogo from '@app/assets/images/alchemy-logo.svg?url'
import AlchemySymbol from '@app/assets/images/alchemy-symbol.svg?url'
import { NavLink, useParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { AnimatePresence } from 'framer-motion'
import { FormattedMessage } from 'react-intl'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import urls from '@app/routes/urls'
import { getLanguageOptions } from '@app/lang/utils'
import type { SupportedLanguage } from '@app/lang/types'
import { updateLanguage } from '@app/store/slices/settings'
import useCampaignHref from '@app/hooks/useCampaignHref'
import { CampaignStatus } from '@app/store/slices/campaign'
import S from './Navbar.styles'
import { CountryField } from '../CountryField'

const Navbar = () => {
  const params = useParams<{ program: string; campaign?: string }>()
  const campaign = useAppSelector(state => state.campaign)
  const dispatch = useAppDispatch()

  const [showMenu, setShowMenu] = useState(false)
  const { language } = useAppSelector(state => state.settings)

  const changeLanguage = useCallback(
    (lang: SupportedLanguage) => {
      dispatch(updateLanguage(lang))
    },
    [dispatch]
  )

  const languageOptions = useMemo(() => {
    return params.program ? getLanguageOptions(params.program, language) : []
  }, [params.program, language])

  const { href: homeUrl } = useCampaignHref({ requestPath: '/' })
  const { href: customerUrl } = useCampaignHref({ requestPath: 'quiz/customer' })
  const { href: faqsUrl } = useCampaignHref({ requestPath: urls.faqs })

  return (
    <>
      <S.Navbar>
        <div>
          <S.Hamburger onClick={() => setShowMenu(!showMenu)}>
            <Icon icon="ic:sharp-menu" fontSize={24} />
          </S.Hamburger>
          {params.program !== 'unieuro' && (
            <NavLink to={homeUrl}>
              <img src={Logo} alt={APP_NAME} />
            </NavLink>
          )}
          <S.LinkList>
            {campaign.status !== CampaignStatus.Closed && (
              <NavLink to={customerUrl}>
                <FormattedMessage id="static.nav.tradein" />
              </NavLink>
            )}
            <NavLink to={faqsUrl}>
              <FormattedMessage id="static.nav.faq" />
            </NavLink>
          </S.LinkList>
        </div>
        <div>
          <CountryField
            value={language}
            options={languageOptions}
            onChange={ev => changeLanguage(ev.currentTarget.value as SupportedLanguage)}
          />
          <S.PoweredBy href={ALCHEMY_URL} target="_blank">
            <FormattedMessage id="static.nav.powered_by" tagName="span" />
            <img src={AlchemyLogo} alt="Alchemy" />
            <img src={AlchemySymbol} alt="Alchemy" />
          </S.PoweredBy>
        </div>
      </S.Navbar>
      <AnimatePresence>
        {showMenu && (
          <S.Drawer
            initial={{ opacity: 0, left: '-100%' }}
            animate={{ opacity: 1, left: '0%' }}
            exit={{ opacity: '-100%' }}>
            <NavLink to={homeUrl}>
              <FormattedMessage id="static.nav.home" />
            </NavLink>

            <NavLink to={faqsUrl}>
              <FormattedMessage id="static.nav.faq" />
            </NavLink>
            <S.CloseButton onClick={() => setShowMenu(false)}>
              <Icon icon="ic:sharp-close" fontSize={32} />
            </S.CloseButton>
          </S.Drawer>
        )}
      </AnimatePresence>
    </>
  )
}

export default Navbar
